/**
 * Clickable token chips for the URL field on Binom edit dialogs. Mirrors the
 * Voluum tokens panel style. Tap a chip to append `?<param>={<param>}` to
 * the URL (idempotent — tokens already present do not duplicate).
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

import { cn } from '@/lib/utils';

import {
  BINOM_URL_TOKENS,
  appendBinomTokenToUrl,
} from './binom-tracking-tokens';

export interface BinomUrlTokensPanelProps {
  url: string;
  disabled?: boolean;
  onUrlChange: (next: string) => void;
}

const BinomUrlTokensPanel: FC<BinomUrlTokensPanelProps> = ({
  url,
  disabled,
  onUrlChange,
}) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="rounded-md border border-border/70 bg-card">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center gap-1.5 px-2 py-1.5 text-left text-[11px] font-semibold text-primary hover:bg-muted/40"
        aria-expanded={expanded}
      >
        {expanded ? (
          <ChevronDown className="h-3 w-3 shrink-0" aria-hidden />
        ) : (
          <ChevronRight className="h-3 w-3 shrink-0" aria-hidden />
        )}
        Tokens
      </button>
      {expanded && (
        <div className="flex flex-wrap gap-1 border-t border-border/60 p-2">
          {BINOM_URL_TOKENS.map((token) => (
            <button
              key={token}
              type="button"
              disabled={disabled}
              onClick={() => onUrlChange(appendBinomTokenToUrl(url, token))}
              className={cn(
                'rounded-md border px-1.5 py-0.5 font-mono text-[10px] leading-none transition-colors',
                'hover:bg-primary/10 hover:text-primary',
                disabled && 'pointer-events-none opacity-50',
                url.includes(token)
                  ? 'border-primary/50 bg-primary/15 text-foreground'
                  : 'border-border/70 bg-muted/50 text-foreground',
              )}
            >
              {token}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default BinomUrlTokensPanel;
